import * as React from 'react';

import { cn } from '@/shared/lib/cn';
import { toLatinDigits } from '@/shared/utils/formatters';

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired when the last box is filled — lets the form auto-submit. */
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  id?: string;
  'aria-describedby'?: string;
}

/**
 * Segmented one-time-code field.
 *
 * Rendered as `length` single-character inputs rather than one text field so
 * mobile keyboards show digits and the code is easy to correct mid-way. Key
 * behaviours: paste fills every box, Backspace on an empty box steps back, and
 * Persian digits are normalised to Latin because that is all the API accepts.
 *
 * The boxes are laid out `dir="ltr"`: a code reads left-to-right even inside an
 * RTL document, and typing must advance in that same direction.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 4,
  disabled,
  invalid,
  autoFocus,
  id,
  'aria-describedby': describedBy,
}: OtpInputProps) {
  const inputsRef = React.useRef<Array<HTMLInputElement | null>>([]);

  const digits = React.useMemo(
    () => Array.from({ length }, (_, index) => value[index] ?? ''),
    [value, length],
  );

  const commit = React.useCallback(
    (next: string) => {
      const trimmed = next.slice(0, length);
      onChange(trimmed);
      if (trimmed.length === length) onComplete?.(trimmed);
    },
    [length, onChange, onComplete],
  );

  const focusBox = (index: number) => {
    inputsRef.current[Math.min(Math.max(index, 0), length - 1)]?.focus();
  };

  const handleChange = (index: number, raw: string) => {
    const cleaned = toLatinDigits(raw).replace(/\D/g, '');
    if (!cleaned) {
      // Deletion inside a box: drop just that character.
      const next = digits.map((digit, i) => (i === index ? '' : digit)).join('');
      commit(next.trimEnd());
      return;
    }

    // Typing many characters at once (paste, or a keyboard suggestion) fills
    // forward from the current box instead of overwriting a single one.
    const next = digits.slice();
    for (let offset = 0; offset < cleaned.length && index + offset < length; offset += 1) {
      next[index + offset] = cleaned[offset]!;
    }
    commit(next.join(''));
    focusBox(index + cleaned.length);
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index]) {
      event.preventDefault();
      const next = digits.slice();
      next[index - 1] = '';
      commit(next.join('').trimEnd());
      focusBox(index - 1);
      return;
    }
    // Arrows follow the visual (LTR) order of the boxes.
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  return (
    <div dir="ltr" className="flex justify-center gap-2" id={id}>
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputsRef.current[index] = element;
          }}
          value={digit}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onFocus={(event) => event.target.select()}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          // `maxLength` of one would truncate a pasted code before onChange
          // ever sees it; the handler enforces the real limit instead.
          aria-label={`رقم ${index + 1} از ${length}`}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={cn(
            'border-input bg-card tabular size-12 rounded-lg border text-center text-lg font-semibold transition-[border-color,box-shadow] outline-none',
            'focus-visible:border-ring focus-visible:ring-ring/25 focus-visible:ring-2',
            'disabled:bg-muted disabled:opacity-60',
            invalid && 'border-destructive focus-visible:ring-destructive/25',
          )}
        />
      ))}
    </div>
  );
}
